import { Box, IconButton, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { VolumeControl } from '../../PlayerControls/VolumeControl'
import { PlayerControlsBar } from '../../PlayerControls/PlayerControlsBar'
import { SpeedControls } from '../../PlayerControls/SpeedControls'
import { normalizeVolume, normalizePlaybackSpeed } from '../../PlayerControls/playerControlsUtils'
import { formatClipAbsoluteTime } from '../utils/playbackUtils'
import { useHtmlVideoPlayerState } from './useHtmlVideoPlayerState'

// Icon imports
import EngagementReviewMuteIcon from '../../../../../assets/images/icons/engagement-review-mute-icon.svg?react'
import EngagementReviewSoundIcon from '../../../../../assets/images/icons/engagement-review-sound-icon.svg?react'
import ExpandVideoContentIcon from '../../../../../assets/images/icons/expand-video-content-icon.svg?react'
import CollapseVideoContentIcon from '../../../../../assets/images/icons/collapse-video-content-icon.svg?react'

interface InspectModePlayerControlsProps {
  video: HTMLVideoElement
  startTimeInMs: number
  // The playing clip's duration per the recordings API (endTime - startTime), shown until the loaded
  // media reports its real (finite) duration - avoids a 0:00 flash on mount and across source swaps.
  fallbackDurationInSeconds: number
  isSidePanelOpen: boolean
  onToggleSidePanel: () => void
}

export const InspectModePlayerControls: FC<InspectModePlayerControlsProps> = ({
  video,
  startTimeInMs,
  fallbackDurationInSeconds,
  isSidePanelOpen,
  onToggleSidePanel
}) => {
  const { currentTimeInSeconds, durationInSeconds, isPlaying, isBuffering, speed } = useHtmlVideoPlayerState(video, fallbackDurationInSeconds)

  const [playerMuted, setPlayerMuted] = useState(false)
  const [playerVolume, setPlayerVolume] = useState(normalizeVolume(video.volume * 100))

  const currentSpeed = normalizePlaybackSpeed(speed)
  const formattedStartTime = formatClipAbsoluteTime(startTimeInMs, currentTimeInSeconds)

  const handlePlayPause = () => {
    if (isPlaying) {
      video.pause()
    } else {
      video.play()
    }
  }

  const handlePlayerVolumeChange = (_event: Event, newValue: number | number[]) => {
    const newVolume = newValue as number
    video.volume = newVolume / 100
    setPlayerVolume(newVolume)
    setPlayerMuted(false)
  }

  const handleTogglePlayerMute = () => {
    setPlayerMuted(previouslyMuted => {
      if (previouslyMuted) {
        video.volume = playerVolume / 100
        return false
      } else {
        video.volume = 0
        return true
      }
    })
  }

  return (
    <PlayerControlsBar
      isPlaying={isPlaying}
      isBuffering={isBuffering}
      onPlayPause={handlePlayPause}
      currentTimeInSeconds={currentTimeInSeconds}
      durationInSeconds={durationInSeconds}
      testIdPrefix="engagement-review-player-controls"
    >
      {formattedStartTime && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              height: '1.5rem',
              borderLeft: '1px solid var(--neutral-300)',
              borderRight: '1px solid var(--neutral-300)',
              px: 'var(--spacing-s)',
              mr: 'var(--spacing-s)',
              display: 'flex',
              alignItems: 'center'
            }}
            data-testid="engagement-review-player-controls-start-time"
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--yellow-800)', whiteSpace: 'nowrap' }}>
              {formattedStartTime}
            </Typography>
          </Box>
        </Box>
      )}

      <SpeedControls currentSpeed={currentSpeed} onSpeedChange={speedOption => (video.playbackRate = speedOption)} />

      <VolumeControl
        onClick={handleTogglePlayerMute}
        playerVolume={playerVolume}
        playerMuted={playerMuted}
        onChange={handlePlayerVolumeChange}
        icons={{
          mutedIcon: <EngagementReviewMuteIcon fontSize="small" />,
          unmutedIcon: <EngagementReviewSoundIcon fontSize="small" />
        }}
        testIds={{
          volumeSlider: 'engagement-review-player-controls-volume-slider',
          muteButton: 'engagement-review-player-controls-mute-button',
          unmuteButton: 'engagement-review-player-controls-unmute-button'
        }}
      />

      <IconButton
        onClick={onToggleSidePanel}
        size="small"
        sx={{ ml: 'var(--spacing-s)' }}
        data-testid={isSidePanelOpen ? 'engagement-review-player-controls-collapse-button' : 'engagement-review-player-controls-expand-button'}
      >
        {isSidePanelOpen ? <ExpandVideoContentIcon /> : <CollapseVideoContentIcon />}
      </IconButton>
    </PlayerControlsBar>
  )
}
